import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { router, type RouteName } from '../../router';
import { store } from '../../store';
import { t } from '../../i18n';
import { icon, type IconName } from '../ui/icons';
import { SETTINGS_SECTIONS } from './settings-sections';
import { moduleNavItems } from '../../modules/registry';

export interface NavItem {
  name: RouteName; path: string; labelKey: string; icon: IconName;
  requires?: string;          // shown only if the user holds this permission
  requiresAny?: string[];     // shown if the user holds ANY of these permissions
  requiresFeature?: string;   // shown only if this feature flag is enabled
}

const ITEMS: NavItem[] = [
  { name: 'dashboard', path: '/admin/dashboard', labelKey: 'common.nav.dashboard', icon: 'home' },
  { name: 'students', path: '/admin/students', labelKey: 'common.nav.students', icon: 'user', requires: 'students.view' },
  { name: 'teachers', path: '/admin/teachers', labelKey: 'common.nav.teachers', icon: 'graduation-cap', requires: 'settings.edit' },
  { name: 'families', path: '/admin/families', labelKey: 'common.nav.families', icon: 'users', requires: 'payments.view' },
  { name: 'classes', path: '/admin/classes', labelKey: 'common.nav.classes', icon: 'align-left', requires: 'classes.view' },
  { name: 'events', path: '/admin/events', labelKey: 'common.nav.events', icon: 'calendar', requires: 'events.view', requiresFeature: 'feature_events' },
  { name: 'mailing', path: '/admin/mailing', labelKey: 'common.nav.mailing', icon: 'mail', requires: 'mailing.view', requiresFeature: 'feature_mailing' },
  { name: 'media', path: '/admin/media', labelKey: 'common.nav.media', icon: 'image', requires: 'media.view', requiresFeature: 'feature_media' },
  { name: 'accounting', path: '/admin/accounting', labelKey: 'common.nav.accounting', icon: 'chart-line', requires: 'accounting.view' },
  { name: 'reporting', path: '/admin/reporting', labelKey: 'common.nav.reporting', icon: 'chart-bar', requiresAny: ['accounting.view', 'students.view'] },
  { name: 'audit', path: '/admin/audit', labelKey: 'common.nav.audit', icon: 'activity', requires: 'settings.view' },
  { name: 'settings', path: '/admin/settings', labelKey: 'common.nav.settings', icon: 'settings', requires: 'settings.view' },
];

/** <cadence-sidebar> — primary admin navigation. */
@customElement('cadence-sidebar')
export class CadenceSidebar extends LitElement {
  @property() active: RouteName = 'dashboard';
  @property({ attribute: 'settings-section' }) settingsSection = 'general';
  @property({ type: Boolean }) collapsed = false;
  @property({ type: Boolean, attribute: 'mobile-open' }) mobileOpen = false;
  private unsub?: () => void;

  connectedCallback(): void { super.connectedCallback(); this.unsub = store.subscribe(() => this.requestUpdate()); }
  disconnectedCallback(): void { super.disconnectedCallback(); this.unsub?.(); }

  static styles = css`
    :host { display: contents; }
    .overlay {
      position: fixed; inset: 0; background: rgba(2, 6, 23, 0.5); z-index: 40;
      opacity: 0; pointer-events: none; transition: opacity 0.2s;
    }
    nav {
      background: var(--color-sidebar-bg); color: var(--color-sidebar-text);
      width: var(--sidebar-width); flex-shrink: 0;
      display: flex; flex-direction: column;
      height: 100vh; position: sticky; top: 0;
      transition: width 0.2s;
    }
    :host([collapsed]) nav { width: var(--sidebar-width-collapsed); }
    .brand {
      display: flex; align-items: center; gap: 10px;
      height: var(--header-height); padding: 0 var(--space-4);
      font-weight: 700; color: var(--color-sidebar-heading);
      border-bottom: 1px solid var(--color-sidebar-border);
      overflow: hidden;
    }
    .brand .dot { width: 12px; height: 12px; border-radius: 50%; background: var(--color-primary); flex-shrink: 0; }
    .brand .logo { width: 32px; height: 32px; border-radius: 50%; object-fit: cover; flex-shrink: 0; background: #fff; }
    /* Wrap a long school name to at most 2 lines (slightly smaller, tighter) instead
       of clipping it; a tooltip carries the rest in the rare 3+ line case. */
    .brand .name {
      min-width: 0; font-size: 1.05rem; line-height: 1.2;
      display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
      overflow: hidden; overflow-wrap: anywhere;
    }
    ul { list-style: none; margin: 0; padding: var(--space-3); display: flex; flex-direction: column; gap: 4px; flex: 1; min-height: 0; overflow-y: auto; }
    a {
      display: flex; align-items: center; gap: 12px;
      padding: 10px 12px; border-radius: var(--radius-md);
      color: var(--color-sidebar-text); text-decoration: none; font-weight: 500;
      white-space: nowrap; overflow: hidden; transition: background 0.15s, color 0.15s;
    }
    a:hover { background: var(--color-sidebar-hover); color: var(--color-sidebar-heading); }
    a[aria-current='page'] { background: var(--color-sidebar-active); color: var(--color-sidebar-active-text); }
    svg { width: 20px; height: 20px; flex-shrink: 0; }
    /* Nested settings sub-sections: shown under the Settings item while that section
       is active. Indented to align past the parent icon; the active sub-page carries
       the solid highlight, the parent acts as a group header. */
    .sub { list-style: none; margin: 2px 0 4px; padding: 0; display: flex; flex-direction: column; gap: 2px; }
    .sub a { padding: 7px 12px 7px 44px; gap: 10px; font-weight: 500; font-size: 0.86rem; }
    .sub a svg { width: 16px; height: 16px; }
    a.group-head { color: var(--color-sidebar-heading); font-weight: 600; }
    :host([collapsed]) .label, :host([collapsed]) .brand .name, :host([collapsed]) .sub { display: none; }
    .collapse {
      margin: var(--space-3); padding: 8px; border-radius: var(--radius-md);
      background: transparent; border: 1px solid var(--color-sidebar-border);
      color: var(--color-sidebar-text); cursor: pointer; font: inherit;
    }
    .collapse:hover { background: var(--color-sidebar-hover); }

    @media (max-width: 1024px) { .collapse { display: none; } }
    @media (max-width: 768px) {
      nav {
        position: fixed; top: 0; left: 0; z-index: 50;
        transform: translateX(-100%); transition: transform 0.2s;
        width: var(--sidebar-width);
      }
      :host([mobile-open]) nav { transform: none; }
      :host([mobile-open]) .overlay { opacity: 1; pointer-events: auto; }
    }
  `;

  private go(e: Event, item: NavItem) {
    this.goPath(e, item.path);
  }

  private goPath(e: Event, path: string) {
    e.preventDefault();
    router.navigate(path);
    this.dispatchEvent(new CustomEvent('navigate', { bubbles: true, composed: true }));
  }

  /** The Settings sub-sections, nested under the Settings item while it's active. */
  private renderSettingsSub() {
    return html`
      <ul class="sub">
        ${SETTINGS_SECTIONS.filter((s) => !s.requires || store.hasPermission(s.requires)).map((s) => {
          const path = `/admin/settings/${s.id}`;
          return html`
            <li>
              <a
                href=${path}
                aria-current=${this.settingsSection === s.id ? 'page' : 'false'}
                title=${t(s.labelKey)}
                @click=${(e: Event) => this.goPath(e, path)}
              >
                ${icon(s.icon, 16)}
                <span class="label">${t(s.labelKey)}</span>
              </a>
            </li>`;
        })}
      </ul>
    `;
  }

  render() {
    return html`
      <div class="overlay" @click=${() => this.dispatchEvent(new CustomEvent('navigate', { bubbles: true, composed: true }))}></div>
      <nav aria-label=${t('common.a11y.primaryNav')}>
        <div class="brand">
          ${store.get().school?.logo_url
            ? html`<img class="logo" src=${store.get().school!.logo_url!} alt="" />`
            : html`<span class="dot"></span>`}
          <span class="name" title=${store.get().school?.name || t('common.app.name')}>${store.get().school?.name || t('common.app.name')}</span>
        </div>
        <ul>
          ${[...ITEMS, ...moduleNavItems()].filter((item) =>
            (!item.requires || store.hasPermission(item.requires)) &&
            (!item.requiresAny || item.requiresAny.some((p) => store.hasPermission(p))) &&
            (!item.requiresFeature || store.featureEnabled(item.requiresFeature)),
          ).map((item) => {
            // While Settings is the active section, nest its sub-sections beneath it
            // (expanded) and let the active sub-page carry the highlight instead.
            const settingsExpanded = item.name === 'settings' && this.active === 'settings' && !this.collapsed;
            return html`
              <li>
                <a
                  href=${item.path}
                  data-section=${item.name}
                  class=${settingsExpanded ? 'group-head' : ''}
                  aria-current=${this.active === item.name && !settingsExpanded ? 'page' : 'false'}
                  title=${t(item.labelKey)}
                  @click=${(e: Event) => this.go(e, item)}
                >
                  ${icon(item.icon, 20)}
                  <span class="label">${t(item.labelKey)}</span>
                </a>
                ${settingsExpanded ? this.renderSettingsSub() : ''}
              </li>
            `;
          })}
        </ul>
        <button
          class="collapse"
          aria-label=${t('common.actions.collapseSidebar')}
          @click=${() => this.dispatchEvent(new CustomEvent('toggle-collapse', { bubbles: true, composed: true }))}
        >
          ${this.collapsed ? '»' : '«'}
        </button>
      </nav>
    `;
  }
}
